import { useEffect, useState } from "react";

export type PublicTocItem = {
  id: string;
  label: string;
};

type PublicTocProps = {
  items: PublicTocItem[];
};

export function PublicToc({ items }: PublicTocProps) {
  const [activeId, setActiveId] = useState<string | null>(items[0]?.id ?? null);

  useEffect(() => {
    if (items.length === 0) {
      return;
    }

    const sections = items
      .map((item) => document.getElementById(item.id))
      .filter((el): el is HTMLElement => el != null);

    if (sections.length === 0) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visible[0]?.target.id) {
          setActiveId(visible[0].target.id);
        }
      },
      {
        rootMargin: "-20% 0px -55% 0px",
        threshold: [0, 0.1, 0.25, 0.5],
      },
    );

    for (const section of sections) {
      observer.observe(section);
    }

    return () => observer.disconnect();
  }, [items]);

  const linkList = (
    <ul className="public-toc-list">
      {items.map((item) => (
        <li key={item.id}>
          <a
            href={`#${item.id}`}
            className={activeId === item.id ? "is-active" : undefined}
            aria-current={activeId === item.id ? "location" : undefined}
          >
            {item.label}
          </a>
        </li>
      ))}
    </ul>
  );

  return (
    <>
      <details className="public-toc-mobile">
        <summary>On this page</summary>
        <nav aria-label="On this page">{linkList}</nav>
      </details>
      <aside className="public-toc-sidebar" aria-label="On this page">
        <p className="public-toc-heading">On this page</p>
        <nav>{linkList}</nav>
      </aside>
    </>
  );
}
